/**
 * N8N Webhook Client
 *
 * Simplified client for interacting with N8N workflows via webhooks.
 * Handles authentication, error handling, and response parsing.
 *
 * Usage:
 *
 * ```javascript
 * import N8NClient from './n8n-client.js';
 * import AuthService from './auth-service.js';
 *
 * const auth = new AuthService(url, key);
 * await auth.initialize();
 *
 * const n8n = new N8NClient('https://flows.smartfront.cl', auth);
 *
 * // Execute workflow
 * const response = await n8n.execute('workflow-id', {
 *     message: 'Hello'
 * });
 * ```
 */

class N8NClient {
    /**
     * @param {string} baseUrl - N8N instance base URL
     * @param {AuthService} authService - Authenticated AuthService instance
     */
    constructor(baseUrl, authService) {
        if (!baseUrl) {
            throw new Error('N8N base URL is required');
        }
        if (!authService) {
            throw new Error('AuthService instance is required');
        }

        this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
        this.authService = authService;
    }

    /**
     * Execute an N8N workflow
     *
     * @param {string} workflowId - Workflow webhook ID
     * @param {object} data - Data to send to workflow
     * @param {object} options - Additional options
     * @param {number} options.timeout - Request timeout in ms (default: 120000)
     * @param {boolean} options.includeUserData - Include user data automatically (default: true)
     * @returns {Promise<object>} Workflow response
     */
    async execute(workflowId, data, options = {}) {
        try {
            if (!this.authService.isAuthenticated()) {
                throw new Error('User must be authenticated');
            }

            const {
                timeout = 120000,  // 2 minutes default
                includeUserData = true
            } = options;

            const url = `${this.baseUrl}/webhook/${workflowId}`;

            // Build request body
            const requestData = {
                ...data
            };

            // Automatically include user data if enabled
            if (includeUserData) {
                const user = this.authService.getUser();
                requestData.user_id = user.id;
                requestData.user_email = user.email;
                requestData.timestamp = new Date().toISOString();
            }

            console.log('[N8NClient] Executing workflow:', workflowId);
            console.log('[N8NClient] Request data:', requestData);

            // Create abort controller for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.authService.getAccessToken()}`
                    },
                    body: JSON.stringify(requestData),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[N8NClient] Workflow error:', errorText);
                    throw new Error(`HTTP ${response.status}: ${errorText}`);
                }

                const responseText = await response.text();

                if (!responseText.trim()) {
                    console.warn('[N8NClient] Empty response from workflow');
                    return { success: true, data: null };
                }

                const result = JSON.parse(responseText);
                console.log('[N8NClient] Workflow response:', result);

                return result;
            } catch (error) {
                clearTimeout(timeoutId);

                if (error.name === 'AbortError') {
                    throw new Error(`Workflow timed out after ${timeout}ms`);
                }
                throw error;
            }
        } catch (error) {
            console.error('[N8NClient] Execution error:', error);
            throw error;
        }
    }

    /**
     * Upload a file to an N8N workflow
     *
     * @param {string} workflowId - Workflow webhook ID
     * @param {File} file - File to upload
     * @param {object} additionalData - Additional form data
     * @param {object} options - Additional options
     * @returns {Promise<object>} Workflow response
     */
    async uploadFile(workflowId, file, additionalData = {}, options = {}) {
        try {
            if (!this.authService.isAuthenticated()) {
                throw new Error('User must be authenticated');
            }

            const { timeout = 300000 } = options; // 5 minutes for file uploads

            const url = `${this.baseUrl}/webhook/${workflowId}`;
            const user = this.authService.getUser();

            const formData = new FormData();
            formData.append('file', file);
            formData.append('user_id', user.id);
            formData.append('user_email', user.email);
            formData.append('timestamp', new Date().toISOString());

            // Add additional data
            Object.keys(additionalData).forEach(key => {
                formData.append(key, additionalData[key]);
            });

            console.log('[N8NClient] Uploading file:', file.name);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.authService.getAccessToken()}`
                    },
                    body: formData,
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const result = await response.json();
                console.log('[N8NClient] Upload response:', result);

                return result;
            } catch (error) {
                clearTimeout(timeoutId);

                if (error.name === 'AbortError') {
                    throw new Error(`Upload timed out after ${timeout}ms`);
                }
                throw error;
            }
        } catch (error) {
            console.error('[N8NClient] Upload error:', error);
            throw error;
        }
    }

    /**
     * Execute workflow with streaming response
     * For long-running workflows that return data incrementally
     *
     * @param {string} workflowId - Workflow webhook ID
     * @param {object} data - Data to send
     * @param {Function} onChunk - Callback for each data chunk
     * @param {object} options - Additional options
     * @returns {Promise<void>}
     */
    async executeStreaming(workflowId, data, onChunk, options = {}) {
        try {
            if (!this.authService.isAuthenticated()) {
                throw new Error('User must be authenticated');
            }

            const { timeout = 300000, includeUserData = true } = options;

            const url = `${this.baseUrl}/webhook/${workflowId}`;

            const requestData = {
                ...data,
                streaming: true  // Signal to N8N that we want streaming
            };

            if (includeUserData) {
                const user = this.authService.getUser();
                requestData.user_id = user.id;
                requestData.user_email = user.email;
                requestData.timestamp = new Date().toISOString();
            }

            console.log('[N8NClient] Executing streaming workflow:', workflowId);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.authService.getAccessToken()}`
                    },
                    body: JSON.stringify(requestData),
                    signal: controller.signal
                });

                if (!response.ok) {
                    clearTimeout(timeoutId);
                    throw new Error(`HTTP ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        clearTimeout(timeoutId);
                        break;
                    }

                    const chunk = decoder.decode(value, { stream: true });

                    // Parse chunk (assuming JSON lines format)
                    const lines = chunk.split('\n').filter(line => line.trim());

                    for (const line of lines) {
                        try {
                            const data = JSON.parse(line);
                            onChunk(data);
                        } catch (error) {
                            console.warn('[N8NClient] Failed to parse chunk:', line);
                        }
                    }
                }
            } catch (error) {
                clearTimeout(timeoutId);

                if (error.name === 'AbortError') {
                    throw new Error(`Streaming timed out after ${timeout}ms`);
                }
                throw error;
            }
        } catch (error) {
            console.error('[N8NClient] Streaming error:', error);
            throw error;
        }
    }

    /**
     * Health check for N8N instance
     * @returns {Promise<boolean>} True if N8N is reachable
     */
    async healthCheck() {
        try {
            // Try to reach N8N base URL (some instances have a health endpoint)
            const response = await fetch(`${this.baseUrl}/healthz`, {
                method: 'GET',
                signal: AbortSignal.timeout(5000)
            });

            return response.ok;
        } catch (error) {
            console.warn('[N8NClient] Health check failed:', error);
            return false;
        }
    }

    /**
     * Get N8N base URL
     * @returns {string}
     */
    getBaseUrl() {
        return this.baseUrl;
    }
}

// Export for ES modules
export default N8NClient;

// Also export as window global for non-module usage
if (typeof window !== 'undefined') {
    window.N8NClient = N8NClient;
}
